'use client'

import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box, IconButton, Stack, Typography } from '@mui/material'

import type { HolidayImage as HolidayImageData } from '@/src/types/holidays'
import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

import HolidayImage from '../holidayImage'

interface RegionFilterItem {
    id: string
    name: string
    priceLabel: string
    countLabel: string
    image?: HolidayImageData
    selected: boolean
}

interface RegionFilterProps {
    label: string
    clearLabel?: string
    previousLabel: string
    nextLabel: string
    items: RegionFilterItem[]
    onToggle: (id: string) => void
    onClear: () => void
    /** Shown at the end of the label row, e.g. the sort control. Still rendered when there are no items. */
    action?: ReactNode
    isMobile: boolean
}

const CARD_WIDTH = { mobile: 200, desktop: 236 }
const THUMB_SIZE = { mobile: 44, desktop: 52 }
const RTL_FLIP = { '[dir=rtl] &': { transform: 'scaleX(-1)' } }

const RegionFilter: React.FC<RegionFilterProps> = ({
    label,
    clearLabel,
    previousLabel,
    nextLabel,
    items,
    onToggle,
    onClear,
    action,
    isMobile
}) => {
    const hasItems = items.length > 0
    const scrollerRef = useRef<HTMLDivElement>(null)
    const [edges, setEdges] = useState({ canStart: false, canEnd: false })

    // scrollLeft is negative in RTL, so the distance travelled is measured as an absolute value.
    const updateEdges = useCallback(() => {
        const node = scrollerRef.current
        if (!node) return
        const travelled = Math.abs(node.scrollLeft)
        const maxTravel = node.scrollWidth - node.clientWidth
        setEdges({ canStart: travelled > 1, canEnd: travelled < maxTravel - 1 })
    }, [])

    useEffect(() => {
        updateEdges()
        window.addEventListener('resize', updateEdges)
        return () => window.removeEventListener('resize', updateEdges)
    }, [updateEdges, items.length])

    const scrollByPage = (towardsEnd: boolean) => {
        const node = scrollerRef.current
        if (!node) return
        const isRtl = getComputedStyle(node).direction === 'rtl'
        const distance = node.clientWidth * 0.8 * (towardsEnd ? 1 : -1) * (isRtl ? -1 : 1)
        node.scrollBy({ left: distance, behavior: 'smooth' })
    }

    // Arrows float over the row's ends so a hidden one never pushes the first card inwards.
    const arrowSx = {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1,
        boxShadow: 2,
        border: 1,
        borderColor: 'borders.default',
        bgcolor: 'background.paper',
        '&:hover': { bgcolor: 'background.paper', borderColor: 'primary.main' }
    } as const

    return (
        <Stack spacing={1.25}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                useFlexGap
                rowGap={1}
                columnGap={1.5}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    {hasItems && (
                        <Typography
                            sx={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: 'textColors.secondary' }}
                        >
                            {label}
                        </Typography>
                    )}
                    {clearLabel && (
                        <Box
                            component="button"
                            type="button"
                            onClick={onClear}
                            sx={{
                                border: 0,
                                bgcolor: 'transparent',
                                p: 0,
                                cursor: 'pointer',
                                fontSize: isMobile ? 13 : 14,
                                fontWeight: 600,
                                color: 'primary.main'
                            }}
                        >
                            {clearLabel}
                        </Box>
                    )}
                </Stack>
                {action && <Box sx={{ marginInlineStart: 'auto' }}>{action}</Box>}
            </Stack>

            {hasItems && (
                <Box sx={{ position: 'relative' }}>
                    {!isMobile && edges.canStart && (
                        <IconButton
                            aria-label={previousLabel}
                            onClick={() => scrollByPage(false)}
                            size="small"
                            sx={{ ...arrowSx, insetInlineStart: 4 }}
                        >
                            <ChevronLeftIcon sx={RTL_FLIP} />
                        </IconButton>
                    )}

                    <Box
                        ref={scrollerRef}
                        onScroll={updateEdges}
                        role="group"
                        aria-label={label}
                        sx={{
                            display: 'flex',
                            gap: isMobile ? 1.25 : 2,
                            overflowX: 'auto',
                            scrollSnapType: 'x mandatory',
                            scrollbarWidth: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                            py: 0.5
                        }}
                    >
                        {items.map(item => (
                            <Box
                                key={item.id}
                                component="button"
                                type="button"
                                aria-pressed={item.selected}
                                onClick={() => onToggle(item.id)}
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    flexShrink: 0,
                                    width: isMobile ? CARD_WIDTH.mobile : CARD_WIDTH.desktop,
                                    p: isMobile ? 1.25 : 1.5,
                                    border: item.selected ? 2 : 1,
                                    borderColor: item.selected ? 'primary.main' : 'borders.default',
                                    borderRadius: HOLIDAYS_RADIUS.SURFACE,
                                    bgcolor: 'background.paper',
                                    cursor: 'pointer',
                                    textAlign: 'start',
                                    scrollSnapAlign: 'start',
                                    // Keeps the content still when the border thickens on selection.
                                    m: item.selected ? 0 : '1px',
                                    '&:hover': { borderColor: 'primary.main' }
                                }}
                            >
                                <Box
                                    sx={{
                                        width: isMobile ? THUMB_SIZE.mobile : THUMB_SIZE.desktop,
                                        height: isMobile ? THUMB_SIZE.mobile : THUMB_SIZE.desktop,
                                        flexShrink: 0,
                                        borderRadius: HOLIDAYS_RADIUS.NESTED,
                                        overflow: 'hidden',
                                        bgcolor: 'borders.default'
                                    }}
                                >
                                    {item.image && <HolidayImage image={item.image} isMobile={isMobile} />}
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        noWrap
                                        sx={{
                                            fontSize: isMobile ? 13 : 14,
                                            fontWeight: 600,
                                            color: 'textColors.primary'
                                        }}
                                    >
                                        {item.name}
                                    </Typography>
                                    <Typography
                                        noWrap
                                        sx={{
                                            fontSize: isMobile ? 14 : 16,
                                            fontWeight: 700,
                                            color: 'textColors.primary'
                                        }}
                                    >
                                        {item.priceLabel}
                                    </Typography>
                                    <Typography noWrap sx={{ fontSize: 12, color: 'textColors.secondary' }}>
                                        {item.countLabel}
                                    </Typography>
                                </Box>
                                {item.selected && (
                                    <CheckCircleIcon
                                        sx={{
                                            position: 'absolute',
                                            top: 6,
                                            insetInlineEnd: 6,
                                            fontSize: 18,
                                            color: 'primary.main'
                                        }}
                                    />
                                )}
                            </Box>
                        ))}
                    </Box>

                    {!isMobile && edges.canEnd && (
                        <IconButton
                            aria-label={nextLabel}
                            onClick={() => scrollByPage(true)}
                            size="small"
                            sx={{ ...arrowSx, insetInlineEnd: 4 }}
                        >
                            <ChevronRightIcon sx={RTL_FLIP} />
                        </IconButton>
                    )}
                </Box>
            )}
        </Stack>
    )
}

export { RegionFilter }
export type { RegionFilterItem, RegionFilterProps }
export default RegionFilter
